import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Signal, SignalEmailOpenedParticipant, SignalType } from "@types";
import {
  getClassName,
  getSignalAnalysisViewLink,
  getSignalEmailSubject,
  getSignalFullDate,
  getSignalMessageItemId,
  getSignalParticipants,
  getSignalTime,
  getSignalTitle,
} from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import { getThreadTrackStatus, updateReadSignals, updateThreadTrackStatus } from "@store/signals";
import { useMixpanel } from "@taskpane/context";
import { useContentHeight } from "@taskpane/hooks";
import { ChevronLeftIcon, LinkBoldIcon } from "@taskpane/icons";
import {
  Avatar,
  AvatarVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Scrollbar,
  Toggler,
  TogglerSize,
  Tooltip,
  TooltipPosition,
  TooltipSize,
} from "@taskpane/components";
import { EnvelopeIcon, FireIcon } from "./icons";
import {
  EmailOpenedSignalContent,
  NewTeamMemberSignalContent,
  PointOfContactChangedSignalContent,
  SentimentChangedSignalContent,
  UsersChangedSignalContent,
  WelcomeSignalContent,
} from "./components";
import css from "./SignalDetails.module.scss";

export interface SignalDetailsProps {
  signal?: Signal | null;
  onClose?: () => void;
}

const SignalDetails: React.FC<SignalDetailsProps> = ({ signal: signalProp = null, onClose }) => {
  const dispatch = useAppDispatch();

  const isTrackStatusLoading = useAppSelector((state) => state.signals.isLoading.threadTrackStatus);

  const { trackEvent } = useMixpanel();

  const [signal, setSignal] = useState<Signal | null>(signalProp);

  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);

  const title = signal ? getSignalTitle(signal) : "";
  const time = signal ? getSignalTime(signal) : "";
  const timeTooltip = signal ? getSignalFullDate(signal.eventDate) : "";
  const subject = signal ? getSignalEmailSubject(signal) || "No Subject" : "No Subject";
  const signalMessageItemId = signal ? getSignalMessageItemId(signal) : "";
  const signalAnalysisViewLink = signal ? getSignalAnalysisViewLink(signal) : "";
  const signalParticipants = signal
    ? getSignalParticipants(signal)
    : {
        userSide: [],
        prospectSide: [],
      };

  const hasTrackStatus = signal?.eventName === SignalType.EmailOpened;
  const opensCount = hasTrackStatus
    ? signal.eventMetadata?.accessEvents?.results?.length || 0
    : null;

  const attrs = {
    container: {
      className: getClassName(css.container, !signalProp && css.containerClosing),
      onAnimationEnd: () => {
        if (!signalProp) {
          setSignal(null);
        }
      },
    },
    headerWrapper: {
      ref: headerWrapperRef,
      className: css.headerWrapper,
    },
    header: {
      className: css.header,
    },
    closeButtonTooltip: {
      arrow: false,
      position: TooltipPosition.BottomStart,
      positionOffset: 4,
      size: TooltipSize.XS,
      text: "Back to Signal List",
    },
    closeButton: {
      type: "button" as const,
      "aria-label": "Close",
      className: css.closeButton,
      onClick: () => {
        if (onClose) {
          onClose();
        }
      },
    },
    titleWrapper: {
      className: css.titleWrapper,
    },
    title: {
      className: css.title,
    },
    timeTooltip: {
      arrow: false,
      className: css.timeTooltip,
      position: TooltipPosition.BottomEnd,
      positionOffset: 4,
      size: TooltipSize.XS,
      text: timeTooltip,
    },
    time: {
      className: css.time,
    },
    subheader: {
      className: css.subheader,
    },
    subheaderIcon: {
      className: css.subheaderIcon,
    },
    subheaderTextWrapper: {
      className: css.subheaderTextWrapper,
    },
    subject: {
      className: css.subject,
    },
    startedAt: {
      className: css.startedAt,
    },
    sidesWrapper: {
      className: css.sidesWrapper,
    },
    side: {
      className: css.side,
    },
    participants: {
      className: css.participants,
    },
    avatarsWrapper: {
      className: css.avatarsWrapper,
    },
    trackStatus: {
      className: css.trackStatus,
    },
    trackStatusLabel: {
      className: getClassName(
        css.trackStatusLabel,
        hasTrackStatus && opensCount >= 7 && css.trackStatusLabelActive
      ),
    },
    trackStatusToggler: {
      label: "Tracking",
      size: TogglerSize.S,
      checked: hasTrackStatus ? !!signal.eventMetadata?.thread?.onTrack : false,
      isHorizontal: true,
      disabled: isTrackStatusLoading,
      onChange: (checked: boolean) => {
        if (hasTrackStatus) {
          trackEvent(`signalsScreen.${checked ? "trackingOn" : "trackingOff"}Click`);
          dispatch(
            updateThreadTrackStatus({
              threadId: signal.eventMetadata.thread.platformThreadId,
              onTrack: checked,
            })
          );
        }
      },
    },
    content: {
      ref: contentRef,
      className: css.content,
    },
    scrollbar: {
      className: css.scrollbar,
      scrollableNodeProps: {
        ref: scrollbarRef,
      },
    },
    footer: {
      ref: footerRef,
      className: css.footer,
    },
    goToMessageButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Secondary,
      className: css.footerButton,
      onClick: () => {
        trackEvent("signalsScreen.goToMessageClick");
        Office.context.mailbox.displayMessageFormAsync(signalMessageItemId, (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to open message form:", result.error);
          }
        });
      },
    },
    viewAnalysisButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Secondary,
      className: css.footerButton,
      onClick: () => {
        trackEvent("signalsScreen.viewAnalysisClick");
        window.open(signalAnalysisViewLink, "_blank", "noopener,noreferrer");
      },
    },
  };

  const renderSubheader = () => {
    if (
      !signal ||
      signal.eventName === SignalType.NewTeamMember ||
      signal.eventName === SignalType.Welcome ||
      signal.eventName === SignalType.WelcomeBack
    ) {
      return null;
    }

    const startedAtLabel = signal.eventName === SignalType.EmailOpened ? "Started:" : "Sent:";
    const timeString = getSignalFullDate(
      signal.eventName === SignalType.EmailOpened
        ? signal.eventMetadata?.thread?.theFirstTrackedEmail?.sentAt
        : "emailSentAt" in signal.eventMetadata
          ? signal.eventMetadata.emailSentAt
          : signal.eventDate
    );

    return (
      <div {...attrs.subheader}>
        <EnvelopeIcon {...attrs.subheaderIcon} />
        <div {...attrs.subheaderTextWrapper}>
          <span {...attrs.subject}>{subject}</span>
          <span {...attrs.startedAt}>
            <span>{startedAtLabel}</span>
            <span>{timeString}</span>
          </span>
        </div>
      </div>
    );
  };

  const renderParticipants = (participants: SignalEmailOpenedParticipant[]) =>
    participants.map(({ email, name, avatarSrc, isUserSide }) => {
      const attrs = {
        avatarTooltip: {
          className: css.avatarTooltip,
          contentClassName: css.avatarTooltipContent,
          size: TooltipSize.XS,
          title: name || email,
          text: email,
          arrow: false,
          autoPosition: true,
        },
        avatar: {
          className: css.avatar,
          name: name || email,
          src: avatarSrc || "",
          variant: isUserSide ? AvatarVariant.User : AvatarVariant.Prospect,
        },
      };

      return (
        <Tooltip {...attrs.avatarTooltip} key={email}>
          <Avatar {...attrs.avatar} />
        </Tooltip>
      );
    });

  const renderSides = () => {
    const { userSide, prospectSide } = signalParticipants;

    if (!userSide.length && !prospectSide.length) {
      return null;
    }

    const participantsToRenderCount = 3;
    const userSideParticipants = userSide.slice(0, participantsToRenderCount);
    const prospectSideParticipants = prospectSide.slice(0, participantsToRenderCount);

    return (
      <div {...attrs.sidesWrapper}>
        <div {...attrs.side}>
          <span>Your side</span>
          <div {...attrs.participants}>
            <div {...attrs.avatarsWrapper}>{renderParticipants(userSideParticipants)}</div>
            {userSide.length > participantsToRenderCount ? (
              <span>+{userSide.length - participantsToRenderCount}</span>
            ) : null}
          </div>
        </div>
        <div {...attrs.side}>
          <span>Prospect side</span>
          <div {...attrs.participants}>
            <div {...attrs.avatarsWrapper}>{renderParticipants(prospectSideParticipants)}</div>
            {prospectSide.length > participantsToRenderCount ? (
              <span>+{prospectSide.length - participantsToRenderCount}</span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderTrackStatus = () => {
    if (!hasTrackStatus) {
      return null;
    }

    return (
      <div {...attrs.trackStatus}>
        <span {...attrs.trackStatusLabel}>
          {opensCount >= 7 ? <FireIcon /> : null}
          <span>
            Opened {opensCount} time{opensCount > 1 ? "s" : ""}
          </span>
        </span>
        <Toggler {...attrs.trackStatusToggler} />
      </div>
    );
  };

  const renderSignalContent = () => {
    if (!signal) {
      return null;
    }

    switch (signal.eventName) {
      case SignalType.EmailOpened:
        return <EmailOpenedSignalContent signal={signal} scrollbarRef={scrollbarRef} />;
      case SignalType.NewTeamMember:
        return <NewTeamMemberSignalContent signal={signal} />;
      case SignalType.PointOfContactChanged:
        return <PointOfContactChangedSignalContent signal={signal} />;
      case SignalType.SentimentDecreased:
      case SignalType.SentimentIncreased:
        return <SentimentChangedSignalContent signal={signal} />;
      case SignalType.ToAdded:
      case SignalType.ToRemoved:
      case SignalType.CcAdded:
      case SignalType.CcRemoved:
      case SignalType.ToThirdPartyAdded:
      case SignalType.ToThirdPartyRemoved:
      case SignalType.CcThirdPartyAdded:
      case SignalType.CcThirdPartyRemoved:
        return <UsersChangedSignalContent signal={signal} />;
      case SignalType.Welcome:
      case SignalType.WelcomeBack:
        return <WelcomeSignalContent signal={signal} />;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (!signalMessageItemId && !signalAnalysisViewLink) {
      return null;
    }

    return (
      <div {...attrs.footer}>
        {signalMessageItemId ? (
          <Button {...attrs.goToMessageButton}>
            <span>Go to Message</span>
          </Button>
        ) : null}
        {signalAnalysisViewLink ? (
          <Button {...attrs.viewAnalysisButton}>
            <span>View Analysis</span>
            <LinkBoldIcon />
          </Button>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    if (signalProp) {
      setSignal(signalProp);
    }
  }, [signalProp]);

  useEffect(() => {
    if (signal && !signal.read) {
      dispatch(updateReadSignals([signal.eventId]));
    }
  }, [signal]);

  useEffect(() => {
    if (hasTrackStatus && signal.eventMetadata?.thread?.onTrack === null) {
      dispatch(
        getThreadTrackStatus({
          threadId: signal.eventMetadata.thread.platformThreadId,
        })
      );
    }
  }, [hasTrackStatus]);

  useContentHeight(contentRef, [headerWrapperRef, footerRef]);

  if (!signal) {
    return null;
  }

  return createPortal(
    <div {...attrs.container}>
      <div {...attrs.headerWrapper}>
        <div {...attrs.header}>
          <Tooltip {...attrs.closeButtonTooltip}>
            <button {...attrs.closeButton}>
              <ChevronLeftIcon />
            </button>
          </Tooltip>
          <div {...attrs.titleWrapper}>
            <span {...attrs.title}>{title}</span>
            <Tooltip {...attrs.timeTooltip}>
              <span {...attrs.time}>{time}</span>
            </Tooltip>
          </div>
        </div>
        {renderSubheader()}
        {renderSides()}
        {renderTrackStatus()}
      </div>
      <div {...attrs.content}>
        <Scrollbar {...attrs.scrollbar}>{renderSignalContent()}</Scrollbar>
      </div>
      {renderFooter()}
    </div>,
    document.body
  );
};

export default SignalDetails;
